import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import strings from "../../i18n/language";
import { instance } from "../../axios";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const WINDOWS = [7, 30, 90];

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
  marginBottom: 20,
};

// "Chrome · macOS" from a user agent: enough to tell devices apart
const browserOf = (userAgent) => {
  if (!userAgent) return '—';
  const browser = /Edg\//.test(userAgent) ? 'Edge'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : '—';
  const os = /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Android/.test(userAgent) ? 'Android'
    : /Mac OS X/.test(userAgent) ? 'macOS'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Linux/.test(userAgent) ? 'Linux'
    : '';
  return os ? `${browser} · ${os}` : browser;
};

const durationOf = (minutes) => {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
};

const agoOf = (date) => {
  if (!date) return strings.LABEL_CONNECTIONS_NEVER;
  const minutes = Math.max(0, dayjs().diff(dayjs(date), 'minute'));
  if (minutes < 60) return strings.formatString(strings.LABEL_CONNECTIONS_AGO_MINUTES, minutes);
  if (minutes < 48 * 60) return strings.formatString(strings.LABEL_CONNECTIONS_AGO_HOURS, Math.floor(minutes / 60));
  return strings.formatString(strings.LABEL_CONNECTIONS_AGO_DAYS, Math.floor(minutes / (24 * 60)));
};

const Chip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
  }}>
    {label}
  </button>
);

const Th = ({ children, style }) => (
  <th style={{
    textAlign: 'left', fontSize: 11, fontWeight: 600, color: MUTED,
    textTransform: 'uppercase', letterSpacing: 0.6, padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap', ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '12px 16px', fontSize: 13, color: 'rgba(232,240,254,0.75)',
    borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle', ...style,
  }}>
    {children}
  </td>
);

const Account = ({ name, id, admin }) => (
  <div>
    <div style={{ color: '#e8f0fe', fontWeight: 500 }}>
      {name || '—'}
      {admin && <span style={{ marginLeft: 6, fontSize: 10, color: BRAND, fontWeight: 700 }}>ADMIN</span>}
    </div>
    <div style={{ fontSize: 11, color: MUTED }}>{id}</div>
  </div>
);

const ConnectionList = () => {
  const [days, setDays] = useState(30);
  const [withAdmins, setWithAdmins] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    instance
      .get('/connection', { params: { days, admins: withAdmins ? 1 : 0 } })
      .then((response) => { if (!cancelled) setData(response.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [days, withAdmins]);

  const loading = !data && !error;
  const emptyRow = (colSpan, text) => (
    <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: 32, fontSize: 13, color: MUTED }}>{text}</td></tr>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header and filters */}
      <div style={{ ...card, padding: '20px 24px' }}>
        <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
          {strings.LABEL_CONNECTIONS_TITLE}
        </Typography>
        <div style={{ fontSize: 12, color: MUTED, margin: '4px 0 16px' }}>{strings.LABEL_CONNECTIONS_SUBTITLE}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {WINDOWS.map((value) => (
            <Chip key={value} label={strings.formatString(strings.LABEL_CONNECTIONS_DAYS, value)}
              active={days === value} onClick={() => setDays(value)} />
          ))}
          <span style={{ width: 8 }} />
          <Chip label={strings.LABEL_CONNECTIONS_WITH_ADMINS} active={withAdmins} onClick={() => setWithAdmins(!withAdmins)} />
        </div>
      </div>

      {/* Accounts */}
      <div style={card}>
        <div style={{ padding: '16px 24px 4px', fontSize: 13, fontWeight: 700, color: '#e8f0fe' }}>
          {strings.LABEL_CONNECTIONS_ACCOUNTS}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>{strings.LABEL_CONNECTIONS_ACCOUNT}</Th>
                <Th>{strings.LABEL_CONNECTIONS_COMPANY}</Th>
                <Th>{strings.LABEL_CONNECTIONS_LAST_SEEN}</Th>
                <Th style={{ width: 90 }}>{strings.LABEL_CONNECTIONS_VISITS}</Th>
                <Th style={{ width: 100 }}>{strings.LABEL_CONNECTIONS_TIME}</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32 }}><CircularProgress size={22} sx={{ color: BRAND }} /></td></tr>}
              {error && emptyRow(5, strings.LABEL_CONNECTIONS_ERROR)}
              {data && data.accounts.length === 0 && emptyRow(5, strings.LABEL_CONNECTIONS_NO_ACCOUNTS)}
              {data && data.accounts.map((account) => (
                <tr key={account.id}>
                  <Td><Account name={account.displayName} id={account.id} admin={account.admin} /></Td>
                  <Td style={{ color: MUTED }}>{account.company || '—'}</Td>
                  <Td>
                    <Tooltip title={account.lastSeen ? dayjs(account.lastSeen).format('LLL') : ''} placement="top">
                      <span style={{ color: account.lastSeen ? '#e8f0fe' : MUTED }}>{agoOf(account.lastSeen)}</span>
                    </Tooltip>
                  </Td>
                  <Td style={{ fontWeight: 700, color: account.visits ? '#e8f0fe' : MUTED }}>{account.visits}</Td>
                  <Td style={{ color: MUTED }}>{durationOf(account.minutes)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visits */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px 4px' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#e8f0fe' }}>{strings.LABEL_CONNECTIONS_VISITS_TITLE}</span>
          {data && data.total > data.visits.length && (
            <span style={{ fontSize: 12, color: MUTED }}>
              {strings.formatString(strings.LABEL_CONNECTIONS_SHOWING, data.visits.length, data.total)}
            </span>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>{strings.LABEL_CONNECTIONS_STARTED}</Th>
                <Th>{strings.LABEL_CONNECTIONS_ACCOUNT}</Th>
                <Th>{strings.LABEL_CONNECTIONS_COMPANY}</Th>
                <Th>{strings.LABEL_CONNECTIONS_DURATION}</Th>
                <Th>{strings.LABEL_CONNECTIONS_ORIGIN}</Th>
                <Th>{strings.LABEL_CONNECTIONS_DEVICE}</Th>
                <Th>IP</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32 }}><CircularProgress size={22} sx={{ color: BRAND }} /></td></tr>}
              {data && data.visits.length === 0 && emptyRow(7, strings.LABEL_CONNECTIONS_NO_VISITS)}
              {data && data.visits.map((visit) => (
                <tr key={visit.id}>
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>{dayjs(visit.startedAt).format('LLL')}</Td>
                  <Td><Account name={visit.displayName} id={visit.user_id} /></Td>
                  <Td style={{ color: MUTED }}>{visit.company || '—'}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{durationOf(visit.minutes)}</Td>
                  <Td>
                    <span style={{
                      fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '3px 9px', whiteSpace: 'nowrap',
                      background: visit.viaLogin ? 'rgba(59,149,232,0.14)' : 'rgba(255,255,255,0.05)',
                      color: visit.viaLogin ? BRAND : MUTED,
                    }}>
                      {visit.viaLogin ? strings.LABEL_CONNECTIONS_VIA_LOGIN : strings.LABEL_CONNECTIONS_VIA_SESSION}
                    </span>
                  </Td>
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap' }}>
                    <Tooltip title={visit.userAgent || ''} placement="top"><span>{browserOf(visit.userAgent)}</span></Tooltip>
                  </Td>
                  <Td style={{ color: MUTED, fontFamily: 'monospace', fontSize: 12 }}>{visit.ip || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Box>
  );
};

export default ConnectionList;
